import { useAuth } from "@clerk/clerk-react";
import { LoadingButton } from "@mui/lab";
import {
  Box,
  Divider,
  Fab,
  FormControlLabel,
  FormHelperText,
  Grid,
  Link,
  ListItemText,
  MenuItem,
  MenuList,
  Switch,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { AlertContext } from "contexts/Alert";
import { DiscoverViewContext } from "contexts/DiscoverView";
import { OrganizationUserContext } from "contexts/Organization";
import { OutreachTemplatesContext } from "contexts/OutreachTemplates";
import { VaSettingsContext } from "contexts/VaSettings";
import lodash from "lodash";
import { useContext, useEffect, useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  BentoSearchFilter,
  BulkSendSettingsStatus,
  OrganizationNiche,
  OrganizationProfile,
  VaCollabPreference,
} from "schemas/dashboard";
import { routes } from "schemas/routes";

import ConfirmDialog from "components/Dialogs/ConfirmDialog";
import { fetcherAuth } from "utils/api";
import { trackEvent } from "utils/tracking";

import BrandPreferencesSettings from "./BrandPreferences";
import EmailWarmupSection from "./EmailWarmupSection";
import OnboardingSettings from "./OnboardingSettings";
import VirtualAssistantStatus from "./VirtualAssistantStatus";
import YourNiches from "./YourNiches";
import { NicheTab, SelectedSetting } from "./schema";
import styles from "./styles";

type EditSettingBody = {
  status?: BulkSendSettingsStatus;
  isUgcCreator?: boolean;
  isInfluencer?: boolean;
  otherType?: string;
  includeBentoSearchFilterIds?: number[];
  excludeBentoSearchFilterIds?: number[];
  openToGifted?: VaCollabPreference;
  openToAffiliates?: VaCollabPreference;
  openToForms?: VaCollabPreference;
  openToSocial?: VaCollabPreference;
  emailWarmupEnabled?: boolean;
};

const SETTINGS_MENU = [
  {
    key: SelectedSetting.niche,
    title: "Your Niches",
  },
  {
    key: SelectedSetting.general,
    title: "General Settings",
  },
  {
    key: SelectedSetting.onboarding,
    title: "Onboarding",
  },
  {
    key: SelectedSetting.payInfo,
    title: "Brand Pay Info",
  },
  {
    key: SelectedSetting.preferences,
    title: "Brand Preferences",
  },
  {
    key: SelectedSetting.emailWarmup,
    title: "Email Warmup",
  },
  {
    key: SelectedSetting.help,
    title: "Help & FAQ",
  },
];

export default function GeneralSettings() {
  const theme = useTheme();
  const { templates } = useContext(OutreachTemplatesContext);
  const { lowerWidth } = useContext(DiscoverViewContext);
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const isMobileScreen = isSmallScreen || lowerWidth;

  const {
    bulkSendSetting,
    setBulkSendSetting,
    fallbackTemplates,
    setFallbackTemplates,
  } = useContext(VaSettingsContext);

  const [confirmCancel, setConfirmCancel] = useState<boolean>(false);
  const [nicheLoading, setNicheLoading] = useState<boolean>(false);
  const [niches, setNiches] = useState<OrganizationNiche[]>([]);
  const {
    profile,
    setProfile,
    organizationVaProfile,
    setOrganizationVaProfile,
    includedBentoSearchFilterIds,
    excludedBentoSearchFilterIds,
    setIncludedBentoSearchFilterIds,
    setExcludedBentoSearchFilterIds,
  } = useContext(OrganizationUserContext);
  const [selectedSetting, setSelectedSetting] = useState<SelectedSetting>(
    SelectedSetting.niche,
  );
  const [localStatus, setLocalStatus] = useState<BulkSendSettingsStatus>(
    bulkSendSetting.status || BulkSendSettingsStatus.active,
  );
  const { setErrorAlert, setAlert } = useContext(AlertContext);
  const { currentOrg } = useContext(OrganizationUserContext);
  const { getToken } = useAuth();
  const [vaSearchFilters, setVaSearchFilters] = useState<BentoSearchFilter[]>(
    [],
  );
  const [isEdited, setIsEdited] = useState<boolean>(false);
  const [localProfile, setLocalProfile] = useState<OrganizationProfile | null>(
    profile,
  );
  const [
    localIncludedBentoSearchFilterIds,
    setLocalIncludedBentoSearchFilterIds,
  ] = useState<number[]>(includedBentoSearchFilterIds);
  const [
    localExcludedBentoSearchFilterIds,
    setLocalExcludedBentoSearchFilterIds,
  ] = useState<number[]>(excludedBentoSearchFilterIds);
  const [loading, setLoading] = useState<boolean>(false);
  const [warningDialog, setWarningDialog] = useState<boolean>(false);
  const [localEmailWarmupEnabled, setLocalEmailWarmupEnabled] =
    useState<boolean>(organizationVaProfile?.emailWarmupEnabled ?? false);

  const fetchFilters = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/bento-search-filters?for_va=true`,
        "GET",
      );
      setVaSearchFilters(res.bentoSearchFilters);
    } catch (error) {
      setErrorAlert(error);
    }
  };

  const getNiches = async (currentTab: NicheTab) => {
    let url = `/api/organization/${currentOrg?.id}/organization-niches/v2?is_archived=false`;
    if (currentTab === NicheTab.active) {
      url += "&statuses=active";
    }

    setNicheLoading(true);
    try {
      const { niches } = await fetcherAuth(getToken, url, "GET");
      setNiches(niches);
      return niches;
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setNicheLoading(false);
    }
  };

  useEffect(() => {
    if (currentOrg?.id && templates?.length > 0) {
      fetchFilters();
      getNiches(NicheTab.active);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  const saveChanges = async () => {
    const body: EditSettingBody = {};
    if (localStatus !== bulkSendSetting.status) {
      body.status = localStatus;
    }
    if (localProfile?.isUgcCreator !== profile?.isUgcCreator) {
      body.isUgcCreator = localProfile?.isUgcCreator;
    }
    if (localProfile?.isInfluencer !== profile?.isInfluencer) {
      body.isInfluencer = localProfile?.isInfluencer;
    }
    if (localProfile?.otherType !== profile?.otherType) {
      body.otherType = localProfile?.otherType;
    }

    if (
      !lodash.isEqual(
        localExcludedBentoSearchFilterIds,
        excludedBentoSearchFilterIds,
      )
    ) {
      body.excludeBentoSearchFilterIds = localExcludedBentoSearchFilterIds;
    }
    if (
      !lodash.isEqual(
        localIncludedBentoSearchFilterIds,
        includedBentoSearchFilterIds,
      )
    ) {
      body.includeBentoSearchFilterIds = localIncludedBentoSearchFilterIds;
    }

    if (organizationVaProfile) {
      body.openToGifted = organizationVaProfile.openToGifted;
      body.openToAffiliates = organizationVaProfile.openToAffiliates;
      body.openToForms = organizationVaProfile.openToForms;
      body.openToSocial = organizationVaProfile.openToSocial;
    }

    if (
      localEmailWarmupEnabled !==
      (organizationVaProfile?.emailWarmupEnabled ?? false)
    ) {
      body.emailWarmupEnabled = localEmailWarmupEnabled;
    }

    setLoading(true);

    try {
      const { bulkSendSetting } = await fetcherAuth(
        getToken,
        `/api/organization/${currentOrg?.id}/va-settings`,
        "PUT",
        {},
        body,
      );
      setBulkSendSetting(bulkSendSetting);
      if (
        localStatus === BulkSendSettingsStatus.inactive &&
        bulkSendSetting.status === BulkSendSettingsStatus.active
      ) {
        setWarningDialog(true);
      }
      if (localProfile) {
        setProfile(localProfile);
      }
      if (body.emailWarmupEnabled !== undefined) {
        setOrganizationVaProfile((v) =>
          v ? { ...v, emailWarmupEnabled: body.emailWarmupEnabled } : v,
        );
      }
      setIncludedBentoSearchFilterIds(localIncludedBentoSearchFilterIds);
      setExcludedBentoSearchFilterIds(localExcludedBentoSearchFilterIds);
      setAlert("Successfully saved settings", "success");
      setIsEdited(false);
    } catch (error) {
      setErrorAlert(error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 0, md: 2 }, py: 2, pb: { xs: 10, md: 2 } }}>
      <Grid container justifyContent="space-between">
        <VirtualAssistantStatus
          bulkSendSetting={bulkSendSetting}
          niches={niches}
          clickToGeneralSettings={() =>
            setSelectedSetting(SelectedSetting.general)
          }
        />

        {isMobileScreen ? (
          <Fab
            variant="extended"
            sx={[styles.fab, isEdited ? styles.activeFab : styles.inactiveFab]}
            onClick={() => saveChanges()}
            size="small"
            disabled={!isEdited}
          >
            Save Updates
          </Fab>
        ) : (
          <Box display="flex" gap={1}>
            <LoadingButton
              variant="contained"
              color="primary"
              sx={[styles.button]}
              disableElevation
              disabled={!isEdited}
              onClick={() => saveChanges()}
              loading={loading}
            >
              Save All Updates
            </LoadingButton>
          </Box>
        )}
      </Grid>

      <Divider sx={{ mt: 2 }} />
      <Grid container>
        {!isMobileScreen && (
          <Grid item xs="auto" lg={3} sx={styles.drawer}>
            <MenuList>
              {SETTINGS_MENU.filter(
                (settingMenu) =>
                  settingMenu.key !== SelectedSetting.emailWarmup ||
                  !bulkSendSetting?.numberOfOverrideBrands,
              ).map((settingMenu) => (
                <MenuItem
                  key={settingMenu.key}
                  onClick={() => {
                    setSelectedSetting(settingMenu.key);
                    trackEvent("VA Settings Page Changed Settings Tab", {
                      tab: settingMenu.title,
                    });
                  }}
                  sx={{ mr: { xs: 1, lg: 10 } }}
                >
                  <ListItemText
                    sx={[
                      selectedSetting === settingMenu.key &&
                        styles.selectedMenuItem,
                    ]}
                  >
                    <Typography variant="h6" sx={styles.menuText}>
                      {settingMenu.title}
                    </Typography>
                  </ListItemText>
                </MenuItem>
              ))}
            </MenuList>
          </Grid>
        )}
        <Grid item xs sx={styles.settingContainer}>
          {(selectedSetting === SelectedSetting.niche || isMobileScreen) && (
            <YourNiches
              bulkSendSetting={bulkSendSetting}
              fallbackTemplates={fallbackTemplates}
              setFallbackTemplates={setFallbackTemplates}
              nicheLoading={nicheLoading}
              getNiches={getNiches}
              niches={niches}
              setNiches={setNiches}
            />
          )}
          {(selectedSetting === SelectedSetting.general || isMobileScreen) && (
            <>
              <FormControlLabel
                control={
                  <Switch
                    checked={localStatus === BulkSendSettingsStatus.inactive}
                    onChange={(e) => {
                      setLocalStatus(
                        e.target.checked
                          ? BulkSendSettingsStatus.inactive
                          : BulkSendSettingsStatus.active,
                      );
                      setIsEdited(true);
                    }}
                  />
                }
                sx={styles.pauseFormControl}
                label="Pause Brand Outreach (Account Remains Active)"
              />
              <FormHelperText sx={{ color: grey[700], mb: 4 }}>
                Once paused, your Virtual Assistant will stop sending new
                outreach messages. Scheduled follow-ups will still go out as
                planned. Your account and progress remain active — you can
                restart outreach anytime.
              </FormHelperText>
            </>
          )}
          {(selectedSetting === SelectedSetting.onboarding ||
            isMobileScreen) && (
            <OnboardingSettings
              vaSearchFilters={vaSearchFilters}
              profile={localProfile}
              setProfile={setLocalProfile}
              includedBentoSearchFilterIds={localIncludedBentoSearchFilterIds}
              excludedBentoSearchFilterIds={localExcludedBentoSearchFilterIds}
              setIncludedBentoSearchFilterIds={
                setLocalIncludedBentoSearchFilterIds
              }
              setIsEdited={setIsEdited}
              setExcludedBentoSearchFilterIds={
                setLocalExcludedBentoSearchFilterIds
              }
            />
          )}
          {(selectedSetting === SelectedSetting.payInfo || isMobileScreen) && (
            <>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", mt: 1, fontWeight: 600 }}
                gutterBottom
              >
                Want Pay Info on a Brand?
              </Typography>
              <Typography paragraph>
                Forward any conversation you're having with a brand about rates
                to <strong>negotiations@onbento.com</strong>, and Bento will
                send you (anonymous) pay data we have about the brand to help
                you negotiate the best rates!
              </Typography>
            </>
          )}
          {(selectedSetting === SelectedSetting.preferences ||
            isMobileScreen) && (
            <Box sx={{ my: 2 }}>
              {isMobileScreen && (
                <Typography
                  variant="h6"
                  sx={{ fontSize: "15px", my: 2, fontWeight: 600 }}
                  gutterBottom
                >
                  Brand Preferences
                </Typography>
              )}
              <BrandPreferencesSettings setIsEdited={setIsEdited} />
            </Box>
          )}
          {!bulkSendSetting?.numberOfOverrideBrands &&
            (selectedSetting === SelectedSetting.emailWarmup ||
              isMobileScreen) && (
              <EmailWarmupSection
                enabled={localEmailWarmupEnabled}
                onToggle={(checked) => {
                  setLocalEmailWarmupEnabled(checked);
                  setIsEdited(true);
                }}
                warmupConfig={bulkSendSetting?.warmupConfig}
                setSelectedSetting={setSelectedSetting}
              />
            )}
          {(selectedSetting === SelectedSetting.help || isMobileScreen) && (
            <>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", fontWeight: 600, mt: 1 }}
                gutterBottom
              >
                Want to stay updated?
              </Typography>
              <Typography paragraph>
                Want a heads-up when your pitches are scheduled each week?
                Download our app to stay in the loop.
              </Typography>
              <Typography paragraph>
                👉 Download our app on{" "}
                <Link
                  href="https://apps.apple.com/us/app/bento-virtual-assistant/id6738798823"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  iOS
                </Link>{" "}
                or{" "}
                <Link
                  href="https://play.google.com/store/apps/details?id=com.onbento.bento"
                  target="_blank"
                >
                  Android
                </Link>
              </Typography>

              <Typography
                variant="h6"
                sx={{ fontSize: "15px", fontWeight: 600 }}
                gutterBottom
              >
                Have questions about the program?
              </Typography>
              <Typography paragraph>
                You can review our commonly asked questions{" "}
                <Link
                  href="https://www.notion.so/hatchways/VA-FAQ-2d38dbc98c0680e4b5ddc6a1702c1b09"
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={() =>
                    trackEvent("VA Settings - FAQ Document Clicked")
                  }
                >
                  here
                </Link>
                .
              </Typography>
              <Typography
                variant="h6"
                sx={{ fontSize: "15px", fontWeight: 600 }}
                gutterBottom
              >
                Need any other help?
              </Typography>
              <Typography paragraph>
                You get a concierge email dedicated only to VA users. Email us
                at{" "}
                <strong>
                  <a href="mailto:va@onbento.com">va@onbento.com</a>
                </strong>{" "}
              </Typography>
            </>
          )}
        </Grid>
      </Grid>

      <ConfirmDialog
        header={`Are you sure?`}
        subheader={`You have some unsaved changes. Are you sure you want to exit and lose all progress?`}
        open={confirmCancel}
        handleClose={() => setConfirmCancel(false)}
        handleConfirm={() => {}}
        cancelText="No"
        confirmText="Yes"
      />

      <ConfirmDialog
        header={`⚠️ Warning`}
        subheader={
          <>
            <Typography paragraph>
              <strong>Outreach has been paused.</strong>
            </Typography>
            <Typography paragraph>
              Your Virtual Assistant will no longer send <em>new</em> outreach
              emails, but <strong>any emails already scheduled</strong> will
              still be delivered as planned. You can resume outreach at any
              time.
            </Typography>
            <Typography>
              If you'd like to review or delete scheduled emails, head to your{" "}
              <Link component={RouterLink} to={`/${routes.inbox}/scheduled`}>
                Scheduled Emails
              </Link>{" "}
              page.
            </Typography>
          </>
        }
        open={warningDialog}
        handleConfirm={() => setWarningDialog(false)}
        confirmText="Okay"
      />
    </Box>
  );
}
